import apiClient from './api';

// Base API path for certification assignments
const BASE_PATH = 'api/certification-assignments';

// ==================== Types & Interfaces ====================

export interface CertificationAssignmentSettings {
  deadline?: string;
  is_required?: boolean;
  notes?: string;
  notification_enabled?: boolean;
}

export interface CheckDuplicatesRequest {
  type: 'users' | 'groups' | 'roles';
  target_ids: (string | { group_key: string; role_key: string })[];
  certifications_ids: string[];
}

export interface CheckDuplicatesResponse {
  has_conflicts: boolean;
  conflicts: {
    target_id: string;
    target_type: string;
    certification_id: string;
    conflict_type: string;
    existing_assignments: any[];
    affected_users?: string[];
  }[];
  summary: {
    total_checked: number;
    total_conflicts: number;
    conflicts_by_type: {
      duplicate: number;
      pending: number;
    };
  };
}

export interface AssignToUsersRequest {
  certification_ids: string[];
  user_ids: string[];
  deadline_days?: number;
  is_mandatory?: boolean;
  notes?: string;
  send_notification?: boolean;
}

export interface AssignToGroupsRequest {
  certification_ids: string[];
  group_keys: string[];
  deadline_days?: number;
  is_mandatory?: boolean;
  notes?: string;
  send_notification?: boolean;
}

export interface AssignToRolesRequest {
  certification_ids: string[];
  role_keys: string[];
  deadline_days?: number;
  is_mandatory?: boolean;
  notes?: string;
  send_notification?: boolean;
}

export interface AssignmentResult {
  certification_id: string;
  target_id: string;
  target_type: 'USER' | 'GROUP' | 'ROLE';
  status: 'success' | 'failed';
  assignment_id?: string;
  error?: string;
}

export interface AssignmentResponse {
  total_assignments?: number;
  successful_assignments?: number;
  failed_assignments?: number;
  results?: AssignmentResult[];
  // Additional fields from group/role assignment
  total_users_assigned?: number;
  certifications_assigned?: number;
  assignment_ids?: string[];
  notifications_sent?: number;
  notification_failures?: number;
  message?: string;
  // Alternate count field names from different API responses
  successful_assignments_count?: number;
  successful?: number;
  failed_assignments_count?: number;
  failed?: number;
  total_assignments_count?: number;
  total?: number;
  // Skipped items from duplicate detection
  skipped_users?: { user_id: string; user_email: string; user_name: string; certification_id: string; reason: string }[];
  skipped_groups?: { group_key: string; group_name: string; certification_id: string; reason: string }[];
  skipped_roles?: { group_key: string; role_key: string; role_name: string; certification_id: string; reason: string }[];
}

export interface BulkImportRequest {
  csv_content: string;
  is_base64: boolean;
}

export interface ValidateBulkImportRequest {
  csv_content: string;
  is_base64: boolean;
}

export interface ValidateBulkImportResponse {
  total_records: number;
  valid_count: number;
  invalid_count: number;
  validation_results: {
    row_number: number;
    valid: boolean;
    errors: string[];
  }[];
}

export interface BulkImportResult {
  row_number: number;
  status: 'success' | 'failed';
  type: string;
  identifier: string;
  certification_slug: string;
  assignment_id?: string;
  error?: string;
}

export interface BulkImportResponse {
  import_id: string;
  total_records: number;
  successful_records: number;
  failed_records: number;
  processing_time_ms: number;
  results: BulkImportResult[];
  summary: {
    by_type: {
      USER: number;
      GROUP: number;
      ROLE: number;
    };
    by_status: {
      success: number;
      failed: number;
    };
  };
}

export interface Assignment {
  assignment_id: string;
  certification_id: string;
  certification_name: string;
  user_id: string;
  user_name: string;
  assignment_type: 'USER' | 'GROUP' | 'ROLE';
  assigned_date: string;
  deadline?: string;
  is_required: boolean;
  status: 'pending' | 'in_progress' | 'completed' | 'overdue' | 'cancelled';
  progress_percentage: number;
  completed_date?: string;
  score?: number;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface AssignmentListParams {
  page?: number;
  limit?: number;
  certification_id?: string;
  status?: 'pending' | 'in_progress' | 'completed' | 'overdue' | 'cancelled';
  assignment_type?: 'USER' | 'GROUP' | 'ROLE';
}

export interface PaginatedAssignmentResponse {
  data: Assignment[];
  metadata: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

export interface BulkImportHistoryParams {
  status?: 'PROCESSING' | 'COMPLETED' | 'FAILED';
  result_status?: 'success' | 'failed';
  imported_by?: string;
  start_date?: string;
  end_date?: string;
  search_identifier?: string;
  search_certification_slug?: string;
  page?: number;
  limit?: number;
  sort_by?: 'import_date' | 'total_records' | 'success_count' | 'failed_count';
  sort_order?: 'asc' | 'desc';
}

export interface BulkImportHistoryItem {
  import_id: string;
  import_date: string;
  imported_by: string;
  status: 'PROCESSING' | 'COMPLETED' | 'FAILED';
  total_records: number;
  success_count: number;
  failed_count: number;
  processing_time_ms: number;
  file_name?: string;
  results?: BulkImportResult[];
}

export interface BulkImportHistoryResponse {
  imports: BulkImportHistoryItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

export interface OverviewAnalytics {
  totalAssigned: number;
  totalCompleted: number;
  completionRate: number;
  avgProgress: number;
  avgTimeSpent: number;
  avgScore: number;
  onTimeRate: number;
  engagementScore: number;
  periodComparison: {
    totalAssigned: {
      current: number;
      previous: number;
      change: number;
      trend: 'up' | 'down' | 'stable';
    };
    completionRate: {
      current: number;
      previous: number;
      change: number;
      trend: 'up' | 'down' | 'stable';
    };
  };
}

// ==================== Service Methods ====================

/**
 * Check for duplicate certification assignments before creating new ones
 */
export async function checkForDuplicates(request: CheckDuplicatesRequest): Promise<CheckDuplicatesResponse> {
  try {
    const response = await apiClient.post(`${BASE_PATH}/check-duplicates`, request);
    return response.data.data;
  } catch (error: any) {
    console.error('Error checking for duplicates:', error);
    throw new Error(error?.response?.data?.description || 'Failed to check for duplicates');
  }
}

/**
 * Assign certifications to individual users
 */
export async function assignToUsers(request: AssignToUsersRequest): Promise<AssignmentResponse> {
  try {
    const response = await apiClient.post(`${BASE_PATH}/assign/users`, request);
    return response.data.data;
  } catch (error: any) {
    console.error('Error assigning to users:', error);
    throw error;
  }
}

/**
 * Assign certifications to groups
 */
export async function assignToGroups(request: AssignToGroupsRequest): Promise<AssignmentResponse> {
  try {
    const response = await apiClient.post(`${BASE_PATH}/assign/groups`, request);
    return response.data.data;
  } catch (error: any) {
    console.error('Error assigning to groups:', error);
    throw error;
  }
}

/**
 * Assign certifications to roles
 */
export async function assignToRoles(request: AssignToRolesRequest): Promise<AssignmentResponse> {
  try {
    const response = await apiClient.post(`${BASE_PATH}/assign/roles`, request);
    return response.data.data;
  } catch (error: any) {
    console.error('Error assigning to roles:', error);
    throw error;
  }
}

/**
 * Bulk import certification assignments from CSV
 */
export async function bulkImport(request: BulkImportRequest): Promise<BulkImportResponse> {
  try {
    const response = await apiClient.post(`${BASE_PATH}/bulk-import`, request);
    return response.data.data;
  } catch (error: any) {
    console.error('Error with bulk import:', error);
    throw new Error(error?.response?.data?.description || 'Failed to import certification assignments');
  }
}

/**
 * Validate CSV for bulk import without creating assignments
 */
export async function validateBulkImport(request: ValidateBulkImportRequest): Promise<ValidateBulkImportResponse> {
  try {
    const response = await apiClient.post(`${BASE_PATH}/validate`, request);
    return response.data.data;
  } catch (error: any) {
    console.error('Error validating bulk import:', error);
    throw error;
  }
}

/**
 * Get assignment by ID
 */
export async function getAssignmentById(assignmentId: string): Promise<any> {
  try {
    const response = await apiClient.get(`${BASE_PATH}/${assignmentId}`);
    console.log('Assignment details API response:', response.data);
    return response.data.data;
  } catch (error: any) {
    console.error('Error getting assignment:', error);
    throw new Error(error?.response?.data?.description || 'Failed to get assignment details');
  }
}

/**
 * List assignments with pagination and filters
 * POST /api/certification-assignments/list
 */
export async function listAssignments(params: AssignmentListParams = {}): Promise<any> {
  try {
    const response = await apiClient.post(`${BASE_PATH}/list`, params);
    console.log('List assignments API response:', response.data);
    // API returns { data: { data: [...], total: X, page: "1", limit: "5" } }
    return response.data.data;
  } catch (error: any) {
    console.error('Error listing assignments:', error);
    throw new Error(error?.response?.data?.description || 'Failed to list assignments');
  }
}

/**
 * Update assignment
 */
export async function updateAssignment(
  assignmentId: string,
  updates: {
    deadline?: string;
    is_required?: boolean;
    notes?: string;
    status?: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  }
): Promise<Assignment> {
  try {
    const response = await apiClient.put(`${BASE_PATH}/${assignmentId}`, updates);
    return response.data.data;
  } catch (error: any) {
    console.error('Error updating assignment:', error);
    throw new Error(error?.response?.data?.description || 'Failed to update assignment');
  }
}

/**
 * Delete/Cancel assignment
 */
export async function deleteAssignment(assignmentId: string): Promise<{ success: boolean; message: string }> {
  try {
    const response = await apiClient.delete(`${BASE_PATH}/${assignmentId}`);
    // API returns { success: true, message: 'Assignment cancelled successfully' }
    return response.data.data || { success: true, message: 'Assignment deleted successfully' };
  } catch (error: any) {
    console.error('Error deleting assignment:', error);
    throw new Error(error?.response?.data?.description || 'Failed to delete assignment');
  }
}

/**
 * Get user's certification assignments
 */
export async function getUserAssignments(userId: string): Promise<Assignment[]> {
  try {
    const response = await apiClient.get(`${BASE_PATH}/user/${userId}`);
    return response.data.data;
  } catch (error: any) {
    console.error('Error getting user assignments:', error);
    throw new Error(error?.response?.data?.description || 'Failed to get user assignments');
  }
}

/**
 * Get assignment tracking details
 */
export async function getAssignmentTracking(assignmentId: string): Promise<any[]> {
  try {
    const response = await apiClient.get(`${BASE_PATH}/${assignmentId}/tracking`);
    return response.data.data;
  } catch (error: any) {
    console.error('Error getting assignment tracking:', error);
    throw new Error(error?.response?.data?.description || 'Failed to get assignment tracking');
  }
}

/**
 * Get bulk import history with filters
 */
export async function getBulkImportHistory(params: BulkImportHistoryParams = {}): Promise<BulkImportHistoryResponse> {
  try {
    const response = await apiClient.post(`${BASE_PATH}/bulk-certification-history`, params);
    // API returns { data: { data: [...], pagination: {...} } }
    // response.data.data.data is the array, response.data.data.pagination is the pagination
    return {
      imports: response.data.data.data || response.data.data || [],
      pagination: response.data.data.pagination || response.data.pagination || { page: 1, limit: 10, total: 0, total_pages: 0 }
    };
  } catch (error: any) {
    console.error('Error getting bulk import history:', error);
    throw new Error(error?.response?.data?.description || 'Failed to get bulk import history');
  }
}

/**
 * Get overview analytics with granularity in body
 */
export async function getOverviewAnalytics(granularity: 'weekly' | 'monthly' | 'quarterly' | 'yearly' = 'monthly'): Promise<OverviewAnalytics> {
  try {
    const response = await apiClient.post(`${BASE_PATH}/overview`, {
      granularity
    });
    return response.data.data;
  } catch (error: any) {
    console.error('Error getting overview analytics:', error);
    throw new Error(error?.response?.data?.description || 'Failed to get overview analytics');
  }
}

/**
 * Get recent certification assignments
 */
export async function getRecentAssignments(params: {
  page?: number;
  limit?: number;
  status?: string;
  is_mandatory?: boolean;
  assignment_type?: string;
} = {}): Promise<any> {
  try {
    const response = await apiClient.get(`${BASE_PATH}/recent`, { params });
    return response.data.data;
  } catch (error: any) {
    console.error('Error getting recent assignments:', error);
    throw new Error(error?.response?.data?.description || 'Failed to get recent assignments');
  }
}

/**
 * Send reminder notifications for an assignment
 */
export async function sendReminderNotifications(assignmentId: string): Promise<{
  message: string;
  success_count: number;
  failure_count: number;
  notification_ids: string[];
}> {
  try {
    const response = await apiClient.post(`${BASE_PATH}/${assignmentId}/send-reminders`);
    return response.data.data;
  } catch (error: any) {
    console.error('Error sending reminder notifications:', error);
    throw new Error(error?.response?.data?.description || 'Failed to send reminder notifications');
  }
}

/**
 * Revoke a GROUP or ROLE certification assignment
 */
export async function revokeAssignment(assignmentId: string): Promise<{
  assignment_deleted: number;
  certification_trackings_deleted: number;
  exam_attempts_deleted: number;
  certificates_deleted: number;
}> {
  const response = await apiClient.delete(`${BASE_PATH}/revoke/assignment/${assignmentId}`);
  return response.data?.data || response.data;
}

// Export as service object for easier imports
export const certificationAssignmentService = {
  checkForDuplicates,
  assignToUsers,
  assignToGroups,
  assignToRoles,
  bulkImport,
  validateBulkImport,
  getAssignmentById,
  listAssignments,
  updateAssignment,
  deleteAssignment,
  getUserAssignments,
  getAssignmentTracking,
  getBulkImportHistory,
  getOverviewAnalytics,
  getRecentAssignments,
  sendReminderNotifications,
  revokeAssignment
};

export default certificationAssignmentService;
